/**
 * TikTok's regional limits, in one place.
 *
 * The constraint has two halves with different lifetimes: one is a setup gate
 * (an account TikTok will not serve at all), the other is permanent and
 * operational (messages from users in those regions arrive with no content, so
 * the agent can never answer them). Both are surfaced — the first in the
 * pre-connect dialog, the second on the connected channel row — and both read
 * from here so the wording cannot drift between the two.
 *
 * Isomorphic: plain strings, no imports, safe in client components.
 */

/** TikTok's own page: "Access to Business Messaging API" → Regional availability. */
export const TIKTOK_REGIONAL_LIMITS_URL =
  'https://business-api.tiktok.com/portal/docs?id=1832184145137922'

/** Short form for the connected channel row. */
export const TIKTOK_AUDIENCE_LIMIT_BADGE = 'Limited audience'

/**
 * Stated as TikTok's platform limitation, not ours, and no stronger than what
 * TikTok says — their wording is "not yet available", so this must not imply
 * either permanence or an imminent fix.
 */
export const TIKTOK_AUDIENCE_LIMIT_TOOLTIP =
  'TikTok does not yet serve the European Economic Area, Switzerland or the UK. ' +
  'Messages from users in those regions reach this channel without any content, ' +
  'so the agent cannot read or answer them.'

export const TIKTOK_CONNECT_NOTICE_POINTS = [
  {
    title: 'Some Business Accounts cannot be connected.',
    body:
      'If the TikTok Business Account was signed up in the European Economic Area, Switzerland ' +
      'or the UK, TikTok does not allow it to be managed through their API at all. Accounts ' +
      'registered in the US need an additional TikTok data security review on our side.',
  },
  {
    title: 'Part of your audience will be unreachable.',
    body:
      'Messages from TikTok users in the European Economic Area, Switzerland or the UK arrive ' +
      'with no content — not the text, not the sender. The agent cannot see or answer them, and ' +
      'they have to be handled in the TikTok app. This does not go away once you are connected.',
  },
  {
    title: 'Set the account to accept messages from everyone first.',
    body:
      'Do this in the TikTok app before authorizing. Otherwise the account owner has to accept ' +
      'each message request by hand, and the channel looks connected but never receives anything.',
  },
  {
    title: 'Only the customer can start a conversation.',
    body:
      'TikTok has no business-initiated messaging on this API, and replies are limited to a ' +
      '48-hour window after each incoming message.',
  },
] as const
